import React, { useCallback, useEffect, useState } from "react";
import { HiCheckCircle, HiCreditCard, HiExclamationTriangle } from "react-icons/hi2";
import { toast } from "react-toastify";
import apiClient from "../api/apiClient";
import LoadingSpinner from "../components/LoadingSpinner";
import StripeCountrySelectModal from "../modals/StripeCountrySelectModal";
import "./AffiliateDashboard.css";

function AffiliateBilling() {
  const [connect, setConnect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);

  const fetchConnect = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiClient.get("/StripeConnectViewSet/");
      if (result?.ok) setConnect(result.data);
      else setConnect(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnect();
  }, [fetchConnect]);

  const startOnboarding = async (countryCode) => {
    try {
      setActionLoading(true);
      const result = await apiClient.get(
        `/StripeAccountViewSet/create_stripe_account/?country_code=${encodeURIComponent(countryCode)}`
      );
      if (!result?.ok || !result?.data?.account_link) {
        toast.error("Unable to start Stripe onboarding. Please try again.");
        return;
      }
      window.location.assign(result.data.account_link);
    } finally {
      setActionLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      setActionLoading(true);
      const result = await apiClient.get("/StripeAccountViewSet/create_login_link/");
      if (!result?.ok || !result?.data?.account_link) {
        toast.error("Unable to open your Stripe dashboard. Please try again.");
        return;
      }
      window.open(result.data.account_link, "_blank", "noopener,noreferrer");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5 py-5">
        <LoadingSpinner color="#FF3E1F" />
      </div>
    );
  }

  const isOnboarded = Boolean(connect?.onboarded);

  return (
    <main className="affiliate-dashboard px-md-3 px-2 pb-4">
      <div className="container py-3 px-3 px-md-4">
        <section className="affiliate-dashboard__hero mb-4">
          <p className="affiliate-dashboard__eyebrow mb-2">Affiliate Hub</p>
          <h1 className="affiliate-dashboard__title mb-2">Billing Information</h1>
          <p className="affiliate-dashboard__subtitle mb-0">
            Connect Stripe to receive affiliate commission payouts.
          </p>
        </section>

        <section className="affiliate-dashboard__panel affiliate-dashboard__stripe-card">
          <div className="affiliate-dashboard__panel-heading">
            <div className="affiliate-dashboard__stripe-heading">
              <span className="affiliate-dashboard__stripe-icon">
                <HiCreditCard size={24} />
              </span>
              <div>
                <p className="affiliate-dashboard__panel-kicker mb-1">Payout account</p>
                <h2 className="affiliate-dashboard__panel-title mb-0">Stripe Connect</h2>
              </div>
            </div>
            <span className={`affiliate-dashboard__status affiliate-dashboard__status--${isOnboarded ? "ready" : "pending"}`}>
              {isOnboarded ? "Connected" : "Not connected"}
            </span>
          </div>

          <p className="affiliate-dashboard__panel-copy">
            Stripe Connect is required before affiliate commissions can be paid
            out to you.
          </p>

          <div className="affiliate-dashboard__stripe-notice">
            {isOnboarded ? (
              <HiCheckCircle size={20} aria-hidden="true" />
            ) : (
              <HiExclamationTriangle size={20} aria-hidden="true" />
            )}
            <span>
              {isOnboarded
                ? "Your Stripe account is ready to receive payouts."
                : "Complete onboarding to activate referral-link sharing and payouts."}
            </span>
          </div>

          <div className="affiliate-dashboard__button-row">
            {isOnboarded ? (
              <button
                type="button"
                className="affiliate-dashboard__button affiliate-dashboard__button--primary"
                disabled={actionLoading}
                onClick={openStripeDashboard}
              >
                {actionLoading ? "Opening..." : "Manage Stripe account"}
              </button>
            ) : (
              <button
                type="button"
                className="affiliate-dashboard__button affiliate-dashboard__button--primary"
                disabled={actionLoading}
                onClick={() => setShowCountryModal(true)}
              >
                Start Stripe onboarding
              </button>
            )}
            <button
              type="button"
              className="affiliate-dashboard__button affiliate-dashboard__button--secondary"
              disabled={actionLoading}
              onClick={fetchConnect}
            >
              Refresh status
            </button>
          </div>
        </section>
      </div>

      <StripeCountrySelectModal
        open={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        onConfirm={startOnboarding}
        loading={actionLoading}
      />
    </main>
  );
}

export default AffiliateBilling;
